import { useContext, useRef, useState } from 'react'

import { Button } from '@material-ui/core'
import { ExitToApp } from '@material-ui/icons'
import { EditContext } from 'Components/Hooks/ContextEdit'
import SetState from 'Components/Hooks/SetState'
import useCustomSnackbar from 'Components/UI/Atoms/SnackBar'
import CancelConfirmButtons from 'Components/UI/Molecules/CancelConfirmButtons'
import ComplexDialog from 'Components/UI/Molecules/ComplexDialog'
import ComplexDialogActions from 'Components/UI/Molecules/ComplexDialog/Actions'
import DialogComponent from 'Components/UI/Molecules/Modals/DialogComponent'
import { SUCURSAL_ORIGEN } from 'Constants'
import { interfaces } from 'service/interfaces'

import AsientosContent from './Asientos'
import ComprobantesContent from './Comprobantes'
import HistorialContent from './Historial'
import HomeContent from './Home'
import PaymentMethodContent from './PaymentMethod'

const formatSubtitleDate = (date) => {
  if (!date) return ''
  const parsed = date instanceof Date ? date : new Date(date)
  if (Number.isNaN(parsed.getTime())) return String(date)
  return parsed.toLocaleDateString('es-AR')
}

const COMPROBANTES_FOOTER_HEIGHT = 64

const PaymentOrderEdit = (props) => {
  const { open, handleclose, data, setRender, render, setLoading, permissions } = props
  const [dataInfo, setDataInfo] = useState(data)
  const formikRef = useRef(null)
  const createDismissableSnackbar = useCustomSnackbar()
  const { state, setOpen, setClose } = SetState()
  const [edit] = useContext(EditContext)
  const [generatingComprobante, setGeneratingComprobante] = useState(false)

  const horizontalTabs = [
    {
      id: 0,
      title: 'Inicio',
      color: 'primary',
    },
    {
      id: 1,
      title: 'Medio de pago',
      color: 'primary',
    },
    {
      id: 2,
      title: 'Comprobantes',
      color: 'primary',
    },
    {
      id: 3,
      title: 'Asientos',
      color: 'primary',
    },
    {
      id: 4,
      title: 'Historial',
      color: 'primary',
    },
  ]
  const renderTabs = (index) => {
    switch (index) {
      case 0:
        return (
          <HomeContent
            data={dataInfo}
            setData={setDataInfo}
            formikRef={formikRef}
            permissions={permissions}
          />
        )
      case 1:
        return <PaymentMethodContent data={dataInfo} setData={setDataInfo} formikRef={formikRef} />
      case 2:
        return (
          <div
            style={{
              height: 'calc(100vh - 170px)',
              minHeight: 320,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                height: `calc(100% - ${COMPROBANTES_FOOTER_HEIGHT}px)`,
                minHeight: 0,
                overflowY: 'auto',
              }}
            >
              <ComprobantesContent data={dataInfo} setData={setDataInfo} formikRef={formikRef} />
            </div>

            <div
              style={{
                height: `${COMPROBANTES_FOOTER_HEIGHT}px`,
                flexShrink: 0,
                boxSizing: 'border-box',
                background: '#fff',
                padding: '10px 20px',
                borderTop: '1px solid #ddd',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  color="primary"
                  variant="contained"
                  onClick={handleGenerateComprobante}
                  disabled={!canGenerateComprobante || generatingComprobante}
                >
                  Generar comprobante
                </Button>
              </div>
            </div>
          </div>
        )
      case 3:
        return <AsientosContent data={dataInfo} />
      case 4:
        return <HistorialContent data={dataInfo} />
      default:
        return <div style={{ padding: '20px' }} />
    }
  }

  const origenLabel = dataInfo?.paymentOrder?.origen
    ? SUCURSAL_ORIGEN.find(
        (sucursal) => sucursal.label === dataInfo.paymentOrder.origen.toUpperCase()
      )
    : null
  const empresaId = origenLabel ? origenLabel.id : null
  const toNumber = (value) => {
    const number = Number(value)
    return Number.isFinite(number) ? number : 0
  }

  const saldoOP = toNumber(dataInfo?.info?.saldo_op ?? dataInfo?.paymentOrderModel?.saldo_op)
  const totalAImputar = toNumber(
    dataInfo?.info?.total_a_imputar ?? dataInfo?.paymentOrderModel?.total_a_imputar
  )
  const estadoOrden = dataInfo?.paymentOrder?.estado
  const canGenerateComprobante =
    edit && estadoOrden === 'Borrador' && Math.abs(saldoOP - totalAImputar) < 0.000001

  const handleGenerateComprobante = async () => {
    if (!empresaId || !dataInfo?.paymentOrder?.id || !canGenerateComprobante) return

    setGeneratingComprobante(true)
    try {
      const response = await interfaces.salesPaymentOrders.generatePaymentOrder({
        origen: empresaId,
        orden: dataInfo.paymentOrder.id,
      })

      const updated = await interfaces.salesPaymentOrders.getOneForEdit(
        dataInfo.paymentOrder.id,
        empresaId
      )
      setDataInfo(updated?.data || dataInfo)

      createDismissableSnackbar(response?.data || 'Comprobante generado correctamente', {
        variant: 'success',
      })
    } catch (error) {
      const msg =
        error?.response?.data?.data || error?.response?.data || 'No se pudo generar el comprobante'
      createDismissableSnackbar(msg, {
        variant: 'error',
      })
    } finally {
      setGeneratingComprobante(false)
    }
  }

  const handleDelete = async () => {
    setLoading(true)
    if (!data?.paymentOrder?.id || !empresaId) {
      console.error('Faltan datos para eliminar:', { data: data?.paymentOrder, empresaId })
      setLoading(false)
      return
    }
    await interfaces.salesPaymentOrders
      .deletePaymentOrder(data.paymentOrder.id, empresaId)
      .then((res) => {
        createDismissableSnackbar(res.data, {
          variant: 'success',
        })
        setRender(!render)
        handleclose()
      })
      .catch((err) => {
        createDismissableSnackbar(err.response.data.data, {
          variant: 'error',
        })
      })
    setLoading(false)
    setClose()
  }

  // const getFlatChanges = (original, updated) => {
  //   let diff = {}

  //   const compare = (obj1, obj2) => {
  //     if (!obj1 || !obj2) return

  //     Object.keys(obj2).forEach((key) => {
  //       if (
  //         key === 'history' ||
  //         key === 'asientos' ||
  //         key === 'paymentMethods' ||
  //         key === 'comprobantes'
  //       )
  //         return

  //       if (typeof obj2[key] === 'object' && obj2[key] !== null && !Array.isArray(obj2[key])) {
  //         compare(obj1[key] || {}, obj2[key])
  //       } else if (String(obj2[key] ?? '') !== String(obj1[key] ?? '')) {
  //         diff[key] = obj2[key]
  //       }
  //     })
  //   }

  //   compare(original, updated)
  //   return diff
  // }

  const handleSubmit = async () => {
    if (formikRef.current) {
      await formikRef.current.submitForm()
    }

    // Solo procesar los campos permitidos por el endpoint: observacion y estado
    const body = {}

    // Verificar si la observación cambió
    if (data.paymentOrder?.observacion !== dataInfo.paymentOrder?.observacion) {
      body.observacion = dataInfo.paymentOrder.observacion
    }

    // Verificar si el estado cambió (solo permite cambiar a borrador/estado 1)
    if (
      data.paymentOrder?.estado !== dataInfo.paymentOrder?.estado &&
      dataInfo.paymentOrder?.estado === 'Borrador'
    ) {
      body.estado = 1
    }

    const numChanges = Object.keys(body).length
    if (numChanges === 0) {
      return createDismissableSnackbar('No hay cambios para guardar', {
        variant: 'info',
      })
    }

    setLoading(true)

    await interfaces.salesPaymentOrders
      .update(data.paymentOrder.id, empresaId, body)
      .then((res) => {
        createDismissableSnackbar(res.data, {
          variant: 'success',
        })
        handleclose()
        setRender(!render)
      })
    setLoading(false)
  }

  const title = `Orden de pago a ${dataInfo?.cliente?.nombre || dataInfo?.paymentOrderModel?.nombre || ''}`
  const subtitle = `${dataInfo?.paymentOrder?.id ? `ID ${dataInfo.paymentOrder.id} - ` : ''}Creado el ${formatSubtitleDate(
    dataInfo?.paymentOrder?.fecha
  )}${dataInfo?.paymentOrder?.usuario ? ` por ${dataInfo.paymentOrder.usuario}` : ''}`
  return (
    <>
      <ComplexDialog
        open={open}
        onClose={handleclose}
        tabs={horizontalTabs}
        title={title}
        subtitle={<div>{subtitle}</div>}
        children={renderTabs}
        actions={
          <ComplexDialogActions
            onClose={handleclose}
            handleSubmit={() => handleSubmit()}
            canEdit={!permissions.edit}
            data={dataInfo}
            editMode={true}
            leftButton={{
              icon: <ExitToApp fontSize="small" />,
              title: 'Eliminar',
              variant: 'contained',
              disabled: dataInfo?.paymentOrder?.estado === 'Borrador' ? !edit : true,
              style: {
                background:
                  dataInfo?.paymentOrder?.estado === 'Borrador' ? 'rgb(244, 67, 54)' : '#e0e0e0',
                color: dataInfo?.paymentOrder?.estado === 'Borrador' ? '#fff' : '#adadad',
              },
              onClick: () => {
                setOpen()
              },
            }}
          />
        }
      />
      {state.suspense && (
        <DialogComponent
          open={state.open}
          handleclose={state.loading ? null : setClose}
          widthLarge={500}
          title="¿Desea eliminar la siguiente orden de pago?"
          subtitle="¡Esta acción es irreversible!"
          disableChildHeight
          disableBackdropClick
          disableEscapeKeyDown
          actions={
            <CancelConfirmButtons
              onClickSubmit={() => handleDelete()}
              disabledCancel={state.loading}
              disabledSubmit={state.loading}
              isLoading={state.loading}
            />
          }
        />
      )}
    </>
  )
}

export default PaymentOrderEdit
